import styled, { css, FlattenSimpleInterpolation } from 'styled-components';

interface StyledRobotScheduleEventContentProps {
  $cleaningFlex: number;
  $chargingFlex: number;
  $isPast: boolean;
  $isDisabled?: boolean;
}

export const StyledRobotScheduleEventContent = styled.div<StyledRobotScheduleEventContentProps>`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  opacity: ${({ $isPast }): number => ($isPast ? 0.5 : 1)};

  ${({ theme: { kcc }, $cleaningFlex, $chargingFlex, $isDisabled }): FlattenSimpleInterpolation => css`
    .robot-schedule-event__cleaning {
      flex: ${$cleaningFlex};
      min-height: 0;
      overflow: hidden;
      background-color: ${$isDisabled ? kcc.colors.GRAY_50 : kcc.colors.PETROL[50]};
      border: 2px solid ${$isDisabled ? kcc.colors.GRAY_100 : kcc.colors.PETROL[100]};
      padding: ${kcc.spacing.XXS};
      font-size: ${kcc.typography.FONT_SIZE.XS}px;
      color: ${kcc.colors.TEXT};
      margin: 0;
    }

    .robot-schedule-event__name {
      font-weight: ${kcc.typography.FONT_WEIGHT.EXTRA_BOLD};
      margin: 0 0 2px;
    }

    .robot-schedule-event__time-span {
      font-weight: ${kcc.typography.FONT_WEIGHT.MEDIUM};
      margin: 0;
    }

    .robot-schedule-event__charging {
      flex: ${$chargingFlex};
      min-height: 0;
      background-color: ${$isDisabled ? kcc.colors.GRAY_50 : kcc.colors.PETROL[100]};
      background-image: repeating-linear-gradient(
        107deg,
        transparent,
        transparent 0.5px,
        ${kcc.colors.WHITE} 1px,
        ${kcc.colors.WHITE} 3.5px,
        transparent 4px
      );
      border-left: 2px solid ${$isDisabled ? kcc.colors.GRAY_100 : kcc.colors.PETROL[100]};
      border-right: 2px solid ${$isDisabled ? kcc.colors.GRAY_100 : kcc.colors.PETROL[100]};
      border-bottom: 2px solid ${$isDisabled ? kcc.colors.GRAY_100 : kcc.colors.PETROL[100]};
    }
  `}
`;

interface StyledRobotScheduleResizeLockedPreviewProps {
  $cleaningFraction: number;
  $chargingFraction: number;
}

export const StyledRobotScheduleResizeLockedPreview = styled.div<StyledRobotScheduleResizeLockedPreviewProps>`
  position: relative;
  height: 100%;
  overflow: visible;

  ${({ theme: { kcc }, $cleaningFraction, $chargingFraction }): FlattenSimpleInterpolation => css`
    .robot-schedule-event__resize-valid {
      height: ${$cleaningFraction * 100}%;
      overflow: hidden;
      background-color: ${kcc.colors.PETROL[50]};
      border: 2px solid ${kcc.colors.PETROL[100]};
      border-bottom: 0; /* no seam line towards the error area below */
      padding: ${kcc.spacing.XXS};
      font-size: ${kcc.typography.FONT_SIZE.XS}px;
      color: ${kcc.colors.TEXT};

      .robot-schedule-event__name {
        font-weight: ${kcc.typography.FONT_WEIGHT.EXTRA_BOLD};
        margin: 0 0 2px;
      }

      .robot-schedule-event__time-span {
        font-weight: ${kcc.typography.FONT_WEIGHT.MEDIUM};
        margin: 0;
      }
    }

    .robot-schedule-event__resize-invalid {
      height: ${(1 - $cleaningFraction - $chargingFraction) * 100}%;
      min-height: 8px;
      background-color: ${kcc.colors.RED[50]};
      border-left: 2px solid ${kcc.colors.RED[500]};
      border-right: 2px solid ${kcc.colors.RED[500]};
      /* No top border by design; the bottom border draws the seam towards the charging block. */
      border-bottom: 2px solid ${kcc.colors.RED[500]};
    }

    /* Charging is fixed-duration — it trails the dragged edge instead of stretching with it. */
    .robot-schedule-event__resize-charging {
      height: ${$chargingFraction * 100}%;
      background-color: ${kcc.colors.PETROL[100]};
      background-image: repeating-linear-gradient(
        107deg,
        transparent,
        transparent 0.5px,
        ${kcc.colors.WHITE} 1px,
        ${kcc.colors.WHITE} 3.5px,
        transparent 4px
      );
      border-left: 2px solid ${kcc.colors.PETROL[100]};
      border-right: 2px solid ${kcc.colors.PETROL[100]};
      border-bottom: 2px solid ${kcc.colors.PETROL[100]};
    }
  `}
`;

/* 🚫 badge portaled to document.body, following next to the ns-resize cursor during the drag. */
export const StyledRobotScheduleResizeCursorBadge = styled.span`
  position: fixed;
  z-index: 1000;
  pointer-events: none;
  font-size: 12px;
  line-height: 1;
`;

export const TOOLTIP_MAX_WIDTH_PX = 220;

/* Tooltip portaled to document.body, following the cursor — rendering it inside the event box
   would clip it to the day column's overflow:hidden ancestors. */
export const StyledRobotScheduleResizeTooltip = styled.div`
  position: fixed;
  z-index: 1000;
  width: max-content;
  max-width: ${TOOLTIP_MAX_WIDTH_PX}px;
  pointer-events: none;
  border-radius: 0;

  ${({ theme: { kcc } }): FlattenSimpleInterpolation => css`
    padding: ${kcc.spacing.XS};
    background-color: ${kcc.colors.BLACK};
    color: ${kcc.colors.WHITE};
    font-family: ${kcc.typography.FONT_FAMILY};
    font-size: ${kcc.typography.FONT_SIZE.BASE}px;
    font-weight: ${kcc.typography.FONT_WEIGHT.REGULAR};
    line-height: 20px;
  `}
`;
